// src/components/quality/Dynamics/DynamicsReceiptChannelTable.js

import {Fragment} from "react";

const DynamicsReceiptChannelTable = ({ assessmentsByWeek, fieldOptions, periodType }) => {
  // Извлекаем периоды из assessmentsByWeek (ключи объекта - это метки периодов)
  const periods = Object.keys(assessmentsByWeek).map(label => ({
    label,
    data: assessmentsByWeek[label] || []
  }));
  
  // Сортируем периоды в обратном порядке (от новых к старым)
  const sortedPeriods = periods.reverse().slice(0, 6);
  
  // Формируем данные: канал поступления -> период -> массив оценок
  const data = {};
  
  // Инициализируем структуру данных для каждого канала
  fieldOptions.forEach(option => {
    data[option.option_value] = {};
    
    // Инициализируем каждый период для каждого канала
    periods.forEach(period => {
      data[option.option_value][period.label] = period.data.filter(
        assessment => assessment.receipt_channel === option.option_value
      );
    });
  });
  
  return (
    <div className="table-wrapper" style={{ overflowX: 'auto' }}>
      <h3>Статистика по каналам поступления</h3>
      <table className="stats-table" style={{ minWidth: '800px' }}>
        <thead>
          <tr>
            <th rowSpan="2" style={{ position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 2 }}>Канал поступления</th>
            <th rowSpan="2">Динамика</th>
            {sortedPeriods.map((period, index) => (
              <Fragment key={index}>
                <th colSpan="3">{period.label}</th>
              </Fragment>
            ))}
          </tr>
          <tr>
            {sortedPeriods.map((period, index) => (
              <Fragment key={index}>
                <th>Количество проверок</th>
                <th>Количество ошибок</th>
                <th>% ошибок</th>
              </Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {Object.entries(data).map(([typeValue, weeksData]) => {
            // Находим option_text для канала поступления
            const typeOption = fieldOptions.find(opt => opt.option_value === typeValue);
            const typeName = typeOption ? typeOption.option_text : typeValue;
            
            // Считаем процент ошибок для первых двух периодов для расчета динамики
            const firstPeriodData = sortedPeriods.length > 0 ? weeksData[sortedPeriods[0].label] || [] : [];
            const secondPeriodData = sortedPeriods.length > 1 ? weeksData[sortedPeriods[1].label] || [] : [];
            
            const firstPeriodZeroCount = firstPeriodData.reduce((sum, assessment) => sum + (assessment.zero_count || 0), 0);
            const firstPeriodTotal = firstPeriodData.length;
            const firstPeriodMistakePercent = firstPeriodTotal > 0 ? (firstPeriodZeroCount / firstPeriodTotal) * 100 : 0;
            
            const secondPeriodZeroCount = secondPeriodData.reduce((sum, assessment) => sum + (assessment.zero_count || 0), 0);
            const secondPeriodTotal = secondPeriodData.length;
            const secondPeriodMistakePercent = secondPeriodTotal > 0 ? (secondPeriodZeroCount / secondPeriodTotal) * 100 : 0;
            
            // Рассчитываем разницу в процентах
            let diffText = '';
            let diffColor = '';
            
            // Проверяем, что в обоих периодах есть данные (Total > 0)
            if (firstPeriodTotal > 0 && secondPeriodTotal > 0) {
              const diffPercent = firstPeriodMistakePercent - secondPeriodMistakePercent;
              
              // Округляем до целого числа
              const absDiff = Math.round(Math.abs(diffPercent));
              
              // Если разница 0 или положительная, считаем это улучшением (зеленый цвет)
              if (diffPercent <= 0) {
                // Показываем стрелку вверх только если значение не ноль
                diffText = diffPercent < 0 ? `▲${absDiff}%` : `▲0%`;
                diffColor = 'green';
              } else {
                // Ухудшение (процент ошибок увеличился) - стрелка вниз
                diffText = `▼${absDiff}%`;
                diffColor = 'red';
              }
            }
            
            return (
              <tr key={typeValue}>
                <td style={{ position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 1 }}>{typeName}</td>
                <td style={{ color: diffColor, fontWeight: 'bold' }}>{diffText}</td>
                {sortedPeriods.map(period => {
                  const periodLabel = period.label;
                  const periodAssessments = weeksData[periodLabel] || [];

                  // Считаем общее количество оценок
                  const total = periodAssessments.length;

                  // Считаем общее количество zero_count
                  const zeroCount = periodAssessments.reduce((sum, assessment) => {
                    return sum + (assessment.zero_count || 0);
                  }, 0);

                  // Вычисляем процент ошибок
                  const mistakePercent = total > 0 ? ((zeroCount / total) * 100).toFixed(1) : '';
                  
                  return [
                    <td key={`${typeValue}-total-${periodLabel}`}>{total}</td>,
                    <td key={`${typeValue}-zero-${periodLabel}`}>{zeroCount}</td>,
                    <td key={`${typeValue}-percent-${periodLabel}`}>{total > 0 ? `${mistakePercent}%` : ''}</td>
                  ];
                })}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          {/* Строка с общими данными и динамикой */}
          <tr>
            <td style={{ position: 'sticky', left: 0, backgroundColor: 'white', fontWeight: 'bold', zIndex: 1 }}>Всего</td>
            {/* Рассчитываем динамику для общих данных */}
            {(() => {
              // Считаем общие данные для первых двух периодов
              const firstPeriodLabel = sortedPeriods.length > 0 ? sortedPeriods[0].label : '';
              const secondPeriodLabel = sortedPeriods.length > 1 ? sortedPeriods[1].label : '';
              
              let firstPeriodTotal = 0;
              let firstPeriodZeroCount = 0;
              let secondPeriodTotal = 0;
              let secondPeriodZeroCount = 0;
              
              if (firstPeriodLabel) {
                Object.values(data).forEach(typeData => {
                  const periodAssessments = typeData[firstPeriodLabel] || [];
                  firstPeriodTotal += periodAssessments.length;
                  periodAssessments.forEach(assessment => {
                    firstPeriodZeroCount += assessment.zero_count || 0;
                  });
                });
              }
              
              if (secondPeriodLabel) {
                Object.values(data).forEach(typeData => {
                  const periodAssessments = typeData[secondPeriodLabel] || [];
                  secondPeriodTotal += periodAssessments.length;
                  periodAssessments.forEach(assessment => {
                    secondPeriodZeroCount += assessment.zero_count || 0;
                  });
                });
              }
              
              // Рассчитываем проценты ошибок
              const firstPeriodMistakePercent = firstPeriodTotal > 0 ? (firstPeriodZeroCount / firstPeriodTotal) * 100 : 0;
              const secondPeriodMistakePercent = secondPeriodTotal > 0 ? (secondPeriodZeroCount / secondPeriodTotal) * 100 : 0;
              
              // Рассчитываем разницу в процентах
              let diffText = '';
              let diffColor = '';
              
              // Проверяем, что в обоих периодах есть данные (Total > 0)
              if (firstPeriodTotal > 0 && secondPeriodTotal > 0) {
                const diffPercent = firstPeriodMistakePercent - secondPeriodMistakePercent;
                
                // Округляем до целого числа
                const absDiff = Math.round(Math.abs(diffPercent));
                
                // Если разница 0 или положительная, считаем это улучшением (зеленый цвет)
                if (diffPercent <= 0) {
                  // Показываем стрелку вверх только если значение не ноль
                  diffText = diffPercent < 0 ? `▲${absDiff}%` : `▲0%`;
                  diffColor = 'green';
                } else {
                  // Ухудшение (процент ошибок увеличился) - стрелка вниз
                  diffText = `▼${absDiff}%`;
                  diffColor = 'red';
                }
              }
              
              return <td style={{ color: diffColor, fontWeight: 'bold' }}>{diffText}</td>;
            })()}
            {/* Выводим общие данные по периодам */}
            {sortedPeriods.map(period => {
              const periodLabel = period.label;
              
              // Собираем все оценки для данного типа за период
              let periodTotal = 0;
              let periodZeroCount = 0;
              
              Object.values(data).forEach(typeData => {
                const periodAssessments = typeData[periodLabel] || [];
                periodTotal += periodAssessments.length;
                periodAssessments.forEach(assessment => {
                  periodZeroCount += assessment.zero_count || 0;
                });
              });
              
              // Вычисляем процент ошибок
              const mistakePercent = periodTotal > 0 ? ((periodZeroCount / periodTotal) * 100).toFixed(1) : '';
              
              return [
                <td key={`total-total-${periodLabel}`}>{periodTotal}</td>,
                <td key={`total-zero-${periodLabel}`}>{periodZeroCount}</td>,
                <td key={`total-percent-${periodLabel}`}>{periodTotal > 0 ? `${mistakePercent}%` : ''}</td>
              ];
            })}
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default DynamicsReceiptChannelTable;
